import { useCallback, useMemo } from 'react'

import { Header } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import * as PaymentButtons from '@features/payments/buttons'
import { addressBook } from '@services/sections/lib/address-book'
import { routes } from '@services/sections/modal/routes'
import { BackButton } from '@shared/components/layout'
import {
  BlockchainAddressStatus,
  useBlockchainRecipientAddressWithMetadataQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { PendingAddressDetailBanner } from '../../components/PendingAddressDetailBanner/PendingAddressDetailBanner'

export interface AddressBookDetailHeaderProps {
  /*
   * Recipient Address ID.
   */
  addressId: string
}

export const AddressBookDetailHeader: React.FC<
  AddressBookDetailHeaderProps
> = ({ addressId }) => {
  const modal = useModal()
  const { t } = useTranslation('address-book/containers')

  const { data } = useBlockchainRecipientAddressWithMetadataQuery({
    variables: {
      id: addressId,
    },
  })

  const recipientAddressWithMetadata =
    data?.blockchainRecipientAddressWithMetadata

  const transfer = useCallback(() => {
    modal.router.push({
      pathname: routes.blockchain.sendOnchain.amount,
      query: {
        address: recipientAddressWithMetadata,
        initialCurrency: recipientAddressWithMetadata?.currency ?? undefined,
      },
    })
  }, [modal.router, recipientAddressWithMetadata])

  const transferEnabled = useMemo(
    () =>
      recipientAddressWithMetadata?.status === BlockchainAddressStatus.ACTIVE,
    [recipientAddressWithMetadata?.status],
  )

  if (!recipientAddressWithMetadata) {
    return null
  }

  return (
    <>
      <BackButton section={addressBook} />
      <PendingAddressDetailBanner
        id={recipientAddressWithMetadata.id}
        owner={recipientAddressWithMetadata.owner}
        status={recipientAddressWithMetadata.status}
      />
      <Header variant="secondary">
        <Header.Head
          title={t('address-book-detail-header.title', {
            name: recipientAddressWithMetadata.description,
          })}
        >
          {!recipientAddressWithMetadata.isVaultAddress &&
            data.blockchainRecipientAddressWithMetadata?.currency != null && (
              <PaymentButtons.AccountFunding.Transfer
                currency={data.blockchainRecipientAddressWithMetadata.currency}
                data-testid="transfer-button"
                disabled={!transferEnabled}
                flowType="onchain"
                onClick={transfer}
                variant="primary"
              >
                {t`address-book-detail-header.transfer`}
              </PaymentButtons.AccountFunding.Transfer>
            )}
        </Header.Head>
      </Header>
    </>
  )
}
